import type {
  NalezStav,
  NalezZdroj,
  Pravo,
  RezimDph,
  SmlouvaTyp,
  TranseSpoustec,
  TranseStav,
  UkolStav,
  VadaFazeVzniku,
  VadaStav,
  VyzvaStav,
  ZakazkaStav,
  ZlOceneniTyp,
  ZlStav,
  ZmenaStav,
} from '@helfr/domain';

/**
 * České popisky enumů — názvosloví PŘESNĚ dle @helfr/domain a wireframů
 * (13 §Jazyk: žádná synonyma; terminologie stavby, ne IT).
 */

export const ZAKAZKA_STAV_LABEL: Record<ZakazkaStav, string> = {
  priprava: 'příprava',
  realizace: 'realizace',
  predano: 'předáno',
  zaruka: 'záruka',
  uzavreno: 'uzavřeno',
};

/** Stavové barvy dle wireframů (šedá koncept, modrá v procesu, zelená hotovo). */
export const ZAKAZKA_STAV_BARVA: Record<ZakazkaStav, string> = {
  priprava: 'bg-fill-disabled text-text-secondary',
  realizace: 'bg-info-bg text-info-text',
  predano: 'bg-success-bg text-success-text',
  zaruka: 'bg-warning-bg text-warning-text',
  uzavreno: 'bg-fill-disabled text-text-secondary',
};

export const SMLOUVA_TYP_LABEL: Record<SmlouvaTyp, string> = {
  hlavni: 'hlavní SOD',
  subdodavatelska: 'subdodavatelská',
};

export const REZIM_DPH_LABEL: Record<RezimDph, string> = {
  prenesena_danova_povinnost: 'přenesená daňová povinnost (§ 92e)',
  standardni: 'standardní',
};

export const TRANSE_SPOUSTEC_LABEL: Record<TranseSpoustec, string> = {
  predani_dila: 'předání díla',
  odstraneni_vad_a_nedodelku: 'odstranění vad a nedodělků',
  konec_zaruky: 'konec záruky',
  rucni: 'ruční',
};

export const TRANSE_STAV_LABEL: Record<TranseStav, string> = {
  ceka: 'čeká',
  uvolnitelna: 'uvolnitelná',
  uvolnena: 'uvolněná',
  nahrazena_bankovni_zarukou: 'nahrazena bankovní zárukou',
};

export const TRANSE_STAV_BARVA: Record<TranseStav, string> = {
  ceka: 'bg-fill-disabled text-text-secondary',
  uvolnitelna: 'bg-warning-bg text-warning-text',
  uvolnena: 'bg-success-bg text-success-text',
  nahrazena_bankovni_zarukou: 'bg-info-bg text-info-text',
};

// --- Terén (Blok B): výzvy, úkoly, vady — stavové barvy dle wireframů ---

export const VYZVA_STAV_LABEL: Record<VyzvaStav, string> = {
  vyzvano: 'otevřeno',
  vyjadreno: 'vyjádřeno',
  schvaleno: 'schváleno',
  zamitnuto: 'zamítnuto',
};

export const VYZVA_STAV_BARVA: Record<VyzvaStav, string> = {
  vyzvano: 'bg-warning-bg text-warning-text',
  vyjadreno: 'bg-success-bg text-success-text',
  schvaleno: 'bg-success-bg text-success-text',
  zamitnuto: 'bg-error-bg text-error-text',
};

export const UKOL_STAV_LABEL: Record<UkolStav, string> = {
  novy: 'nový',
  probiha: 'probíhá',
  hotovo: 'hotovo',
  zamitnuto: 'zamítnuto',
};

export const UKOL_STAV_BARVA: Record<UkolStav, string> = {
  novy: 'bg-fill-disabled text-text-secondary',
  probiha: 'bg-info-bg text-info-text',
  hotovo: 'bg-success-bg text-success-text',
  zamitnuto: 'bg-error-bg text-error-text',
};

/** Další stav úkolu pro velké tlačítko v terénu (nový → probíhá → hotovo). */
export const UKOL_DALSI_STAV: Partial<Record<UkolStav, UkolStav>> = {
  novy: 'probiha',
  probiha: 'hotovo',
};

export const VADA_STAV_LABEL: Record<VadaStav, string> = {
  otevreno: 'otevřeno',
  opraveno: 'opraveno',
  prekontrolovano: 'překontrolováno',
};

export const VADA_STAV_BARVA: Record<VadaStav, string> = {
  otevreno: 'bg-error-bg text-error-text',
  opraveno: 'bg-info-bg text-info-text',
  prekontrolovano: 'bg-success-bg text-success-text',
};

/** Další stav vady pro velké tlačítko (otevřeno → opraveno → překontrolováno). */
export const VADA_DALSI_STAV: Partial<Record<VadaStav, VadaStav>> = {
  otevreno: 'opraveno',
  opraveno: 'prekontrolovano',
};

export const VADA_FAZE_LABEL: Record<VadaFazeVzniku, string> = {
  prubezna: 'průběžná',
  prejimka_subdodavky: 'přejímka subdodávky',
  predani_objednateli: 'předání objednateli',
  zaruka: 'záruka',
};

// --- Blok D: klín — kanonický slovník stavů (13 §Jazyk: žádná synonyma) ---

export const ZMENA_STAV_LABEL: Record<ZmenaStav, string> = {
  nova: 'nová',
  kvalifikovana: 'přijato jako vícepráce',
  ocenena: 'oceněna',
  schvalena: 'schválena',
  v_dodatku: 'v dodatku',
  fakturovatelna: 'fakturovatelná',
  zamitnuta: 'zamítnuta',
  sporna: 'sporná',
};

/**
 * Stavové barvy (13 §Stavové barvy): šedá koncept, modrá v procesu, zelená
 * schválená/v dodatku/fakturovatelná, fialová sporné (samostatná kategorie rizika).
 */
export const ZMENA_STAV_BARVA: Record<ZmenaStav, string> = {
  nova: 'bg-fill-disabled text-text-secondary',
  kvalifikovana: 'bg-info-bg text-info-text',
  ocenena: 'bg-info-bg text-info-text',
  schvalena: 'bg-success-bg text-success-text',
  v_dodatku: 'bg-success-bg text-success-text',
  fakturovatelna: 'bg-success-bg text-success-text',
  zamitnuta: 'bg-error-bg text-error-text',
  sporna: 'bg-sporna-bg text-sporna-text',
};

export const ZL_STAV_LABEL: Record<ZlStav, string> = {
  navrzeno: 'navrženo',
  podepsano: 'podepsáno',
  zamitnuto: 'zamítnuto',
  provedeno_na_riziko: 'provedeno na riziko',
  sporne: 'sporné',
};

export const ZL_STAV_BARVA: Record<ZlStav, string> = {
  navrzeno: 'bg-info-bg text-info-text',
  podepsano: 'bg-success-bg text-success-text',
  zamitnuto: 'bg-error-bg text-error-text',
  provedeno_na_riziko: 'bg-warning-bg text-warning-text',
  sporne: 'bg-sporna-bg text-sporna-text',
};

export const ZL_OCENENI_LABEL: Record<ZlOceneniTyp, string> = {
  sod_jednotkova_cena: 'jednotková cena ze SOD',
  cn_s_rozpadem: 'CN s rozpadem',
  vlastni_kalkulace: 'vlastní kalkulace',
  rucni_naklad: 'ruční náklad',
};

export const NALEZ_ZDROJ_LABEL: Record<NalezZdroj, string> = {
  teren: 'NÁLEZ z terénu',
  revize_pd: 'PODNĚT z kanceláře · revize výkresu',
  pozadavek_investora: 'PODNĚT · požadavek investora',
  fakturace: 'PODNĚT · fakturace',
};

export const NALEZ_STAV_LABEL: Record<NalezStav, string> = {
  novy: 'nový',
  kvalifikovan: 'kvalifikován',
  zamitnut: 'zamítnut',
  slouceno: 'sloučeno',
};

export const NALEZ_STAV_BARVA: Record<NalezStav, string> = {
  novy: 'bg-info-bg text-info-text',
  kvalifikovan: 'bg-success-bg text-success-text',
  zamitnut: 'bg-error-bg text-error-text',
  slouceno: 'bg-fill-disabled text-text-secondary',
};

export const PRAVO_LABEL: Record<Pravo, string> = {
  'finance.read': 'finance — čtení',
  'finance.write': 'finance — zápis',
  'projekty.read': 'zakázky — čtení',
  'projekty.write': 'zakázky — zápis',
  'smlouvy.read': 'smlouvy — čtení',
  'smlouvy.write': 'smlouvy — zápis',
  'import.read': 'import — čtení',
  'import.write': 'import — zápis',
  'vykresy.write': 'výkresy — zápis',
  'zmeny.kvalifikovat': 'změny — kvalifikace',
  'zmeny.schvalovat': 'změny — schvalování (ředitel)',
  'denik.write': 'deník — zápis',
  'denik.sign': 'deník — podpis',
  'admin.users': 'správa uživatelů',
  'admin.firma': 'správa firmy',
};
